import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { AiImplementationList } from '@/components/integrations/AiImplementationList';
import { SEO } from '@/components/landing/shared/SEO';

interface AiImplementationPageProps {
    isDashboard?: boolean;
}

const AiImplementationPage = ({ isDashboard = false }: AiImplementationPageProps) => {

    return (
        <div className={cn(
            "relative min-h-screen bg-background text-foreground overflow-hidden",
            isDashboard ? "pt-6 sm:pt-8 pb-12" : "pt-20 pb-20"
        )}>
            {!isDashboard && (
                <SEO
                    title="Conversational AI Implementation Guide - Add an AI Bot to Your Business"
                    description="Step-by-step guide to implementing a conversational AI chatbot for your business. Let customers talk to AI trained on your content and automate customer support."
                    url="https://chytbot.com/ai-implementation"
                    keywords={[
                        "conversational AI",
                        "artificial intelligence chat",
                        "AI bot",
                        "chat AI bot",
                        "AI chat bots",
                        "talk to AI",
                        "GPT chatbot",
                        "chatbot GPT",
                        "AI chat GPT",
                        "AI chatbot GPT",
                        "AI implementation guide",
                        "add AI chatbot to website",
                        "AI customer service bot",
                        "ChytBot",
                    ]}
                />
            )}
            {/* Background Effects */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

            <div className={cn(
                "relative z-10 mx-auto",
                isDashboard ? "px-4 sm:px-6 lg:px-8 max-w-7xl" : "container px-4"
            )}>
                <div className="max-w-3xl mx-auto">
                    <div className="mb-8">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Ask AI to Implement</h1>
                            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                                Don't want to code? Copy one of these prompts and paste it into your AI coding assistant to get ChytBot's conversational AI chatbot added to your site instantly.
                            </p>
                        </motion.div>
                    </div>

                    {/* Prompt Cards */}
                    <AiImplementationList isDashboard={isDashboard} />

                    {/* Footer Hint */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="mt-8 text-center"
                    >
                        <p className="text-xs text-muted-foreground">
                            Make sure to replace <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded border bg-amber-400/10 border-amber-400/20 text-amber-600 dark:text-amber-400 font-mono font-bold text-[10px]" title="Click the badge above to learn where to find your Bot ID">YOUR_BOT_ID</span> with your actual bot ID found in the dashboard.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default AiImplementationPage;
